import { useEffect, useRef, useState } from 'react';
import { Phone, X } from 'lucide-react';

/**
 * Small, NON-fullscreen incoming-call card. Sits centered on top of the
 * hero (with a soft dim behind it, not a full opaque takeover) and runs the
 * 2-ring intro before handing control to the parent so the page's own
 * AvaSphere can animate during the live call.
 *
 * Props:
 *   open:        bool — when true, mount + run the ring sequence
 *   phoneNumber: string
 *   onAnswered:  () => void — fired ~4s after open; parent should kick off Vapi
 *   onCancel:    () => void — user closed the card before the call connected
 */
export default function IncomingCallCard({ open, phoneNumber, onAnswered, onCancel }) {
  const audioCtxRef = useRef(null);
  const [ringIdx, setRingIdx] = useState(0); // 0 = before any ring, 1 = first beep done, 2 = answered

  const playRing = (durationMs) => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
        try { ctx.resume(); } catch {}
      }
      const now = ctx.currentTime;
      const dur = durationMs / 1000;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.1, now + 0.04);
      gain.gain.setValueAtTime(0.1, now + dur - 0.04);
      gain.gain.linearRampToValueAtTime(0, now + dur);
      gain.connect(ctx.destination);
      [440, 480].forEach(freq => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(now);
        osc.stop(now + dur);
      });
    } catch {}
  };

  useEffect(() => {
    if (!open) { setRingIdx(0); return; }
    const t1 = setTimeout(() => { playRing(1400); setRingIdx(1); }, 150);
    const t2 = setTimeout(() => { playRing(1400); setRingIdx(2); }, 2300);
    const t3 = setTimeout(() => { onAnswered && onAnswered(); }, 4000);
    return () => { [t1, t2, t3].forEach(clearTimeout); };
  }, [open]); // eslint-disable-line

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center"
      data-testid="incoming-call-card-overlay"
      // Soft dim, not opaque — the home-page hero stays partially visible
      // behind the card so the transition to the live sphere feels seamless.
      style={{ background: 'rgba(3,7,18,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onCancel}
    >
      <div
        data-testid="incoming-call-card"
        onClick={e => e.stopPropagation()}
        className="relative rounded-3xl border border-white/10 shadow-2xl px-8 py-10 text-center"
        style={{
          width: 360,
          background: 'linear-gradient(180deg, #0a0f1f 0%, #050913 100%)',
          fontFamily: 'monospace',
        }}
      >
        {/* Close X */}
        <button
          data-testid="incoming-call-card-close"
          onClick={onCancel}
          aria-label="Cancel call"
          className="absolute top-3 right-3 text-white/30 hover:text-white/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Phone disc + rings — modern rounded handset on a deep indigo→
            violet vertical gradient, matching the user's reference */}
        <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
          {[0, 1, 2, 3, 4].map(i => (
            <span
              key={i}
              className="absolute inset-0 rounded-full border"
              style={{
                borderColor: 'rgba(120,170,255,0.28)',
                animation: `nbc-card-ring 2.4s ease-out ${i * 0.48}s infinite`,
              }}
            />
          ))}
          <div
            className="absolute left-1/2 top-1/2 flex items-center justify-center"
            style={{
              width: 108,
              height: 108,
              transform: 'translate(-50%, -50%)',
              borderRadius: '999px',
              // Reference uses a vertical light-blue (top) → indigo/violet
              // (bottom) gradient with a subtle dark rim.
              background: 'linear-gradient(180deg, #3b6cff 0%, #5b53f0 55%, #6d3df5 100%)',
              boxShadow:
                '0 14px 50px rgba(96,120,255,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset',
              animation: 'nbc-card-wobble 0.6s ease-in-out infinite',
            }}
          >
            <Phone className="w-14 h-14 text-white" strokeWidth={1.85} />
          </div>
        </div>

        <p className="text-[10px] tracking-[3px] mt-8 mb-1.5 text-blue-300/60">
          INCOMING CALL
        </p>
        <p className="text-xl text-white font-medium tracking-wide" data-testid="incoming-call-card-number">
          {phoneNumber || '+1 (XXX) XXX-XXXX'}
        </p>
        <p className="text-xs mt-2 text-blue-300/60" data-testid="incoming-call-card-status">
          {ringIdx >= 2 ? 'Connecting…' : 'Ringing…'}
        </p>

        <p className="text-[9px] tracking-[2px] text-blue-300/30 mt-5 leading-relaxed">
          THIS DEMO CALL MAY BE RECORDED FOR<br />QUALITY ASSURANCE AND TRAINING.
        </p>
      </div>

      <style>{`
        @keyframes nbc-card-ring {
          0%   { transform: scale(0.55); opacity: 0; }
          15%  { opacity: 0.55; }
          100% { transform: scale(1.05); opacity: 0; }
        }
        @keyframes nbc-card-wobble {
          0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
          25%      { transform: translate(-50%, -50%) rotate(-3deg); }
          75%      { transform: translate(-50%, -50%) rotate(3deg); }
        }
      `}</style>
    </div>
  );
}
